<ng-container *transloco="let t">
  @if (pro$ | async; as pro) {
    <div class="relative">
      @if (pro.publishedProfile || pro.minimalProfile) {
        <bgo-hero-cover
          [showPartnership]="false"
          [showCallToAction]="false"
          [showSellingPointBar]="false"
          [emptyCover]="true"
          [reducedCoverTransparency]="true"
          class="mt-xl h-layout block overflow-y-hidden lg:h-auto"
        >
          <cft-breadcrumbs [breadcrumbs]="breadcrumbs()" [showBackButton]="false"></cft-breadcrumbs>
          @if (pro.publishedProfile?.coverImage?.url || pro.minimalProfile?.coverImage?.url) {
            <img
              [ngSrc]="pro.publishedProfile?.coverImage?.url! || pro.minimalProfile?.coverImage?.url!"
              class="h-full w-full object-cover"
              content="background-image"
              alt=""
              fill
              priority
            />
          }
        </bgo-hero-cover>
        <div class="h-content-layout">
          <div class="flex items-center justify-center">
            <cft-profile-introduction
              [showRecommendedBy]="isRecommendedByShown(pro.recommendingAgency?.name)"
              [pro]="profileIntro(pro)"
              class="z-10 -mt-[80px] w-full"
              [ngClass]="isRecommendedByShown(pro.recommendingAgency?.name) ? 'lg:-mt-[108px]' : 'lg:-mt-[60px]'"
            >
              @if (isRecommendedByShown(pro.recommendingAgency?.name)) {
                <cft-recommended-by>
                  <p content="recommendedByText">{{ t('profile.introduction.recommendedBy') }}</p>
                  <bgo-mobiliar-logo [negative]="true" content="logo" size="xs"></bgo-mobiliar-logo>
                  <p content="agencyName">{{ '|' + pro.recommendingAgency!.name | bgoRemoveAgencyPrefixPipe }}</p>
                </cft-recommended-by>
              }
              @if (pro.locations | bgoAverageGooglePlace; as googlePlace) {
                <cft-google-rating
                  [rating]="googlePlace.rating"
                  [numReviews]="googlePlace.userRatingCount"
                ></cft-google-rating>
              }
              <span content="guaranteeText1">{{ t('profile.introduction.guaranteeText1') }}</span>
              <span content="guaranteeText2">{{ t('profile.introduction.guaranteeText2') }}</span>
              <button cft-button class="btn btn-vermilion" content="requestButton" (click)="startOtherRequest(pro)">
                <div class="flex max-w-fit gap-x-xs">
                  <fa-icon [icon]="faPlus"></fa-icon>
                  {{ t('profile.introduction.ctas.request') }}
                </div>
              </button>
            </cft-profile-introduction>
          </div>
          @if (pro.publishedProfile) {
            <div class="mt-3xl mb-xl grid grid-cols-2 gap-md lg:grid-cols-3 xl:grid-cols-4">
              @if (employeeCard$ | async; as card) {
                <cft-display [description]="t(card.titleKey)">
                  <div content="value">
                    <span>{{ card.numEmployees }}</span>
                    @if (card.numApprentices) {
                      <span class="text-md">/{{ card.numApprentices }}</span>
                    }
                  </div>
                </cft-display>
              }
              @if (pro.publishedProfile.featuredOfferings.length > 0) {
                <cft-display [description]="t('profile.coveredJobs')">
                  <div content="value">{{ pro.publishedProfile.featuredOfferings.length.toString() }}</div>
                </cft-display>
              }
              @if (pro.spokenLanguages.length > 0) {
                <cft-display [description]="t('profile.spokenLangs')">
                  <div
                    content="value"
                    class="flex h-[2rem] flex-row items-center justify-center space-x-sm md:h-[3rem]"
                  >
                    @for (lang of spokenLanguages$ | async; track lang) {
                      <bgo-language-flag [language]="lang" class="h-1/2"></bgo-language-flag>
                    }
                  </div>
                </cft-display>
              }
              @if (pro.publishedProfile.experienceInYears > 0) {
                <cft-display [description]="t('profile.experience.title')">
                  <div content="value">
                    <span>{{ pro.publishedProfile.experienceInYears }}</span>
                    <span class="ml-2xs text-md">{{ t('profile.experience.label') }}</span>
                  </div>
                </cft-display>
              }
            </div>
          }
          @if (offeringSection$ | async; as offeringSection) {
            <div class="mt-2xl mb-xl" data-testid="offerings">
              <bgo-offerings-section
                [offeringsSection]="offeringSection"
                [showOtherJobRequestCard]="false"
                [showOtherJobRequestSection]="true"
                [targetQueryParams]="{pro: pro.slug}"
                titleAlignment="left"
              >
              </bgo-offerings-section>
            </div>
          }
          @if (pro.locations.length > 0) {
            <div id="pro-profile-locations" class="mt-2xl mb-xl">
              <cft-content-section
                [title]="t('profile.locations.title')"
                [plot]="t('profile.locations.plot')"
                titleAlignment="left"
              >
                @if (locations$ | async; as locations) {
                  <cft-locations-map
                    [labels]="{
                      defaultLocation: t('locationsMap.mainLocation'),
                      location: t('locationsMap.location'),
                      associations: t('locationsMap.associations'),
                    }"
                    [locations]="locations"
                    [googleMapsApiKey]="configService.config.googleMaps.apiKey"
                  ></cft-locations-map>
                }
              </cft-content-section>
            </div>
          }
          @if (pro.publishedProfile?.aboutCompanyDescription) {
            <div class="mt-2xl mb-xl">
              <cft-content-section
                [title]="pro.publishedProfile?.aboutCompanyTitle || pro.companyName"
                [plot]="t('profile.about.plot')"
                titleAlignment="left"
              >
                <div class="grid grid-cols-1 gap-lg lg:grid-cols-5 lg:gap-xl">
                  <p class="whitespace-pre-line lg:col-span-3">
                    {{ pro.publishedProfile?.aboutCompanyDescription! }}
                  </p>
                  @if (gallery$ | async; as gallery) {
                    @if (gallery.length > 0) {
                      <div class="row-start-3 lg:col-span-3 lg:row-start-2">
                        <div class="mb-xs heading-xl">{{ t('profile.about.galleryTitle') }}</div>
                        <bgo-gallery [galleryId]="'public-profile-' + pro.slug" [items]="gallery"></bgo-gallery>
                      </div>
                    }
                  }
                  <div class="flex flex-col space-y-xs text-base lg:col-span-2 lg:ml-xl">
                    @if (pro.publishedProfile?.isBuildigoVerified) {
                      <bgo-profile-badge
                        [label]="t('profile.badges.verified')"
                        iconPath="assets/images/profile/badges/buildigo-shield.svg"
                      ></bgo-profile-badge>
                    }
                    @if (pro.publishedProfile?.isMobiliarNetwork) {
                      <bgo-profile-badge
                        [label]="t('profile.badges.fromMobiliar')"
                        iconPath="assets/images/profile/badges/heart.svg"
                      ></bgo-profile-badge>
                    }
                    @if (pro.publishedProfile?.isErneuerbarheizenNetwork) {
                      <bgo-profile-badge
                        [label]="t('profile.badges.fromErneuerbarheizen')"
                        iconPath="assets/images/profile/badges/heart.svg"
                      ></bgo-profile-badge>
                    }
                  </div>
                </div>
              </cft-content-section>
            </div>
          }
        </div>
        <!--        Sticky footer -->
        <bgo-sticky-bottom-navigation
          class="sticky-navigation-show"
          [class.md:!translate-y-full]="isStickyFooterHidden"
          [guaranteeText]="t('profile.guaranteeText')"
        >
        </bgo-sticky-bottom-navigation>
      }
    </div>
  }
</ng-container>
